<script setup lang="ts">
import {computed, reactive, ref} from "vue";
import TcVod from "vod-js-sdk-v6";
import Uploader from "vod-js-sdk-v6/lib/src/uploader";
import {ComponentSize, dayjs, ElNotification, FormInstance, FormRules} from "element-plus";
import {AddLiveSection, CustomFile} from "@/types";
import {VideoService} from "@/api/api.ts";
import AddSingleFile from "components/dialog/common/innner/AddSingleFile.vue";
import AddFakeLiveVideo from "components/dialog/live/inner/AddFakeLiveVideo.vue";
import {TimeFormatter} from "@/utils/common.ts";

const aheadRef = ref<InstanceType<typeof AddSingleFile>>();
const pdfRef = ref<InstanceType<typeof AddSingleFile>>();
const consolidateRef = ref<InstanceType<typeof AddSingleFile>>();
const addFakeLiveVideo = ref<InstanceType<typeof AddFakeLiveVideo>>()

const props =defineProps<{
  is_fake: boolean
}>()

interface RuleForm {
  title: string
}

const videoId = ref('')
const liveDate = ref('')
const startTime = ref('')
const endTime = ref('')
let uploader: Uploader;
// 0: 未上传 1: 上传中 2: 上传成功 3: 上传失败
const uploadStatus = ref(0);
const mediaProgress = ref(0)
const videoDuration = ref(0)

const formSize = ref<ComponentSize>('default')
const ruleFormRef = ref<FormInstance>()
const ruleForm = reactive<RuleForm>({
  title: '',
})
const rules = reactive<FormRules<RuleForm>>({
  title: [
    {required: true, message: '请输入章节标题', trigger: 'blur'},
    {min: 1, max: 30, message: '标题长度在 1 到 30 个字符', trigger: 'blur'},
  ]
})
const emit = defineEmits<{
  (e: 'onSubmit', info: AddLiveSection): void
  (e: "onClose"): void
}>()

const reset = () => {
  ruleForm.title = ''
  videoId.value = ''
  liveDate.value = ''
  startTime.value = ''
  endTime.value = ''
  uploadStatus.value = 0
  mediaProgress.value = 0
  videoDuration.value = 0
  aheadRef.value?.clearFile()
  pdfRef.value?.clearFile()
  consolidateRef.value?.clearFile()
}

const getSignature = async (): Promise<string> => {
  const res = await VideoService.createSignature()
  return res.data.data || ''
}

const tcVod = new TcVod({
  getSignature: getSignature,
})

const onClear = () => {
  videoId.value = ''
  videoDuration.value = 0
  uploadStatus.value = 0
  mediaProgress.value = 0
}

const cancelUpload = () => {
  if (uploader) {
    uploader.cancel()
  }
  videoDuration.value = 0
  videoId.value = ''
  uploadStatus.value = 0
  mediaProgress.value = 0
}

const onVideoFileChange = (file: CustomFile) => {
  if (checkVideoDuration(file)) {
    addFakeLiveVideo.value?.onStartUpload(file)
    upload(file)
  }
}

const upload = (file: CustomFile) => {
  // 视频名称
  if (uploader) {
    uploader.cancel()
  }
  uploader = tcVod.upload({
    mediaFile: file,
  })
  videoId.value = ''
  uploadStatus.value = 1
  mediaProgress.value = 0
  uploader.on("media_progress", info => {
    // 上传进度
    mediaProgress.value = Math.floor(info.percent * 100);
  })
  uploader.on("media_upload", info => {
    // 上传成功
    console.log(info)
  })
  uploader.done().then(res => {
    const id = res.fileId
    // 提交视频
    VideoService.addVideo({
      file_name: file.name,
      video_id: id,
      duration: file.duration || 0,
      width: file.width || 0,
      height: file.height || 0,
    }).then(res => {
      if (res.data.code === 200) {
        videoId.value = id
        uploadStatus.value = 2
        ElNotification.success({
          title: "视频上传成功",
          message: res.data.msg,
          type: "success",
        })
      } else {
        ElNotification.error({
          title: "提示",
          message: res.data.msg,
          type: "error",
        })
      }
    }).catch(err => {
      console.log(err)
      uploadStatus.value = 3
    })
  }).catch(err => {
    console.log(err)
    uploadStatus.value = 3
  })
}

const isLiveDateValid = (): boolean => {
  if (liveDate.value) {
    const date = dayjs(liveDate.value).startOf('day').unix()
    const today = dayjs().startOf('day').unix()
    if (date < today) {
      ElNotification.warning({
        title: "提示",
        message: "直播日期不能小于等于当前日期",
        type: "warning",
      })
      return false
    }
  } else {
    ElNotification.warning({
      title: "提示",
      message: "请选择直播日期",
      type: "warning",
    })
    return false
  }
  return true
}

const isLiveTimeValid = (): boolean => {
  if (!startTime.value) {
    ElNotification.warning({
      title: "提示",
      message: "请选择直播开始时间",
      type: "warning",
    })
    return false
  }
  if (!endTime.value) {
    ElNotification.warning({
      title: "提示",
      message: "请选择直播结束时间",
      type: "warning",
    })
    return false
  }
  if (props.is_fake && videoDuration.value !== 0) {
    const start = dayjs(startTime.value, "HH:mm").unix()
    const end = dayjs(endTime.value, "HH:mm").unix()
    const getLiveDuration = end - start
    if (Math.abs(getLiveDuration - videoDuration.value) > 3 * 60) {
      ElNotification.warning({
        title: "提示",
        message: "直播时长与视频时长误差不能超过 3 分钟",
        type: "warning",
      })
      return false
    }
  }
  return true
}

const checkVideoDuration = (file: CustomFile) => {
  if (startTime.value && endTime.value) {
    videoDuration.value = file.duration || 0
    const start = dayjs(startTime.value, "HH:mm").unix()
    const end = dayjs(endTime.value, "HH:mm").unix()
    const getLiveDuration = end - start
    if (Math.abs(getLiveDuration - videoDuration.value) > 3 * 60) {
      videoDuration.value = 0
      ElNotification.warning({
        title: "提示",
        message: "直播时长与视频时长误差不能超过 3 分钟",
        type: "warning",
      })
      return false
    }
  }
  return true
}

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return
  await formEl.validate((valid, fields) => {
    if (valid) {
      if (isLiveDateValid() && isLiveTimeValid()) {
        const liveDateUnix = dayjs(liveDate.value).startOf('day').unix()
        const startSub = dayjs(startTime.value, 'HH:mm').startOf('minutes').unix() - dayjs().startOf('day').unix()
        const endSub = dayjs(endTime.value, 'HH:mm').startOf('minutes').unix() - dayjs().startOf('day').unix()
        const startTimeUnix = liveDateUnix + startSub
        const endTimeUnix = liveDateUnix + endSub
        emit('onSubmit', {
          title: ruleForm.title,
          live_date: liveDateUnix,
          start_time: startTimeUnix,
          end_time: endTimeUnix,
          video_id: videoId.value,
          ahead_data: aheadRef.value?.getFileId(),
          pdf_data: pdfRef.value?.getFileId(),
          consolidate_data: consolidateRef.value?.getFileId(),
        })
      }
    } else {
      console.log('error submit!', fields)
    }
  })
}

const resetForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return
  formEl.resetFields()
  emit("onClose")
}

const onDateChange = () => {
  console.log(dayjs(liveDate.value).startOf('day').unix())
}

const needDuration = computed(() => {
  if (startTime.value && endTime.value) {
    const start = dayjs(startTime.value, "HH:mm").unix()
    const end = dayjs(endTime.value, "HH:mm").unix()
    const liveDuration = end - start
    return (
        TimeFormatter.secondsFormat(liveDuration - 180) +
        "~" +
        TimeFormatter.secondsFormat(liveDuration + 180)
    );
  }
  return "待定";
});

defineExpose({cancelUpload, reset})
</script>

<template>
  <el-form
      ref="ruleFormRef"
      style="max-width: 40rem"
      :model="ruleForm"
      :rules="rules"
      label-width="auto"
      class="demo-ruleForm"
      :size="formSize"
      status-icon
  >
    <el-form-item label="章节标题" prop="title">
      <el-input v-model="ruleForm.title" placeholder="请输入章节标题" maxlength="30" show-word-limit/>
    </el-form-item>
    <el-form-item label="直播日期">
      <div>
        <div>
          <el-date-picker placeholder="直播日期" v-model="liveDate" type="date" @change="onDateChange"/>
        </div>
        <div class="mt-1rem">
          <el-time-select
              v-model="startTime"
              style="width: 240px"
              :max-time="endTime"
              class="mr-1rem"
              placeholder="直播开始时间"
              start="06:00"
              step="00:05"
              end="23:00"
          />
          <el-time-select
              v-model="endTime"
              style="width: 240px"
              :min-time="startTime"
              placeholder="直播结束时间"
              start="06:00"
              step="00:05"
              end="23:00"
          />
        </div>
        <div class="mt-.5rem font-bold" v-if="is_fake">
          根据您选择的上课时间，您需要上传时长范围为
          <span class="text-red-4">
              {{ needDuration }}
            </span>
          的视频
        </div>
      </div>
    </el-form-item>
    <el-form-item label="直播视频" v-if="is_fake">
      <add-fake-live-video
          ref="addFakeLiveVideo"
          :uploadStatus="uploadStatus"
          :video-progress="mediaProgress"
          @on-clear="onClear"
          @on-cancel-upload="cancelUpload"
          @on-video-file-change="onVideoFileChange"/>
    </el-form-item>
    <el-form-item label="上传导学">
      <add-single-file ref="aheadRef"></add-single-file>
    </el-form-item>
    <el-form-item label="上传讲义">
      <add-single-file ref="pdfRef"></add-single-file>
    </el-form-item>
    <el-form-item label="上传巩固">
      <add-single-file ref="consolidateRef"></add-single-file>
    </el-form-item>
    <el-form-item>
      <el-button type="primary" @click="submitForm(ruleFormRef)">
        创建章节
      </el-button>
      <el-button @click="resetForm(ruleFormRef)">取消</el-button>
    </el-form-item>
  </el-form>
</template>

<style scoped>
</style>
